import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Wrench,
    RefreshCw,
    Loader2,
    AlertTriangle,
    MapPin,
    Package,
    ChevronDown,
    ChevronRight,
    CalendarClock,
    CheckCircle2,
    Clock,
    PowerOff,
    Inbox,
    Layers,
    Calendar,
    Save,
    X,
    ExternalLink,
    FolderTree,
    Users,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const CURRENT_YEAR = new Date().getFullYear();
const NEXT_YEAR = CURRENT_YEAR + 1;

const STATUS_META = {
    0: {
        label: 'Не указан',
        badge: 'bg-slate-600/20 border-slate-600/50 text-slate-400',
        icon: Clock,
        iconCls: 'text-slate-500',
    },
    1: {
        label: 'Введен в эксплуатацию',
        badge: 'bg-green-500/15 border-green-500/40 text-green-400',
        icon: CheckCircle2,
        iconCls: 'text-green-400',
    },
    2: {
        label: 'Ожидание оплаты',
        badge: 'bg-amber-500/15 border-amber-500/40 text-amber-400',
        icon: Clock,
        iconCls: 'text-amber-400',
    },
    3: {
        label: 'На поверке',
        badge: 'bg-blue-500/15 border-blue-500/40 text-blue-400',
        icon: Wrench,
        iconCls: 'text-blue-400',
    },
    4: {
        label: 'Выведен из эксплуатации',
        badge: 'bg-slate-600/20 border-slate-600/50 text-slate-400',
        icon: PowerOff,
        iconCls: 'text-slate-500',
    },
};

const plural = (n, one, few, many) => {
    const value = Math.abs(n ?? 0) % 100;
    const digit = value % 10;
    if (value > 10 && value < 20) return many;
    if (digit === 1) return one;
    if (digit >= 2 && digit <= 4) return few;
    return many;
};

const formatAddress = (node) => {
    const parts = [];
    if (node.city) parts.push(node.city);
    if (node.street) parts.push(node.street);
    if (node.houseNumber) parts.push(`д. ${node.houseNumber}`);
    return parts.length ? parts.join(', ') : 'Адрес не указан';
};

const parseRuDate = (value) => {
    if (!value) return null;
    const parts = value.split('.');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
};

const getVerificationDueMeta = (value) => {
    const date = parseRuDate(value);
    if (!date) {
        return {
            className: 'text-slate-500',
            label: 'Нет даты',
        };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
        return {
            className: 'text-red-400 font-semibold',
            label: 'Просрочена',
        };
    }
    if (date.getFullYear() === CURRENT_YEAR) {
        return {
            className: 'text-amber-400 font-semibold',
            label: 'Текущий год',
        };
    }
    if (date.getFullYear() === NEXT_YEAR) {
        return {
            className: 'text-yellow-300 font-semibold',
            label: 'Следующий год',
        };
    }
    return {
        className: 'text-green-400',
        label: 'Действует',
    };
};

const getCommissioningDateClass = (node) => {
    if (node.status !== 1 || !node.oplombirovanValidUntil) return 'text-slate-300';
    const date = parseRuDate(node.oplombirovanValidUntil);
    if (!date) return 'text-slate-300';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'text-red-400 font-semibold';
    if (diffDays <= 30) return 'text-yellow-400 font-semibold';
    return 'text-green-400 font-semibold';
};

const getCommissioningDaysLeft = (node) => {
    if (node.status !== 1 || !node.oplombirovanValidUntil) return null;
    const date = parseRuDate(node.oplombirovanValidUntil);
    if (!date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
};

// ============================================
// ✅ Модальное окно смены статуса
// ============================================
const EditStatusModal = ({ node, onClose, onSave, saving }) => {
    const [status, setStatus] = useState(node.status || 2);
    const [startDate, setStartDate] = useState(
        node.oplombirovanDate
            ? node.oplombirovanDate.split('.').reverse().join('-')
            : ''
    );
    const [validUntil, setValidUntil] = useState(
        node.oplombirovanValidUntil
            ? node.oplombirovanValidUntil.split('.').reverse().join('-')
            : ''
    );

    const isCommissioned = Number(status) === 1;

    const handleSave = () => {
        onSave({
            status: Number(status),
            oplombirovanDate: isCommissioned ? startDate || null : null,
            oplombirovanValidUntil: isCommissioned ? validUntil || null : null,
        });
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                    <h3 className="text-white font-semibold">
                        Статус узла: {node.nodeName}
                    </h3>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="text-slate-400 hover:text-white disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 space-y-4">
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">
                            Статус узла учета
                        </label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(Number(e.target.value))}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                        >
                            <option value={1}>Введен в эксплуатацию</option>
                            <option value={2}>Ожидание оплаты</option>
                            <option value={3}>На поверке</option>
                            <option value={4}>Выведен из эксплуатации</option>
                        </select>
                    </div>

                    {isCommissioned ? (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700 space-y-3">
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Дата ввода в эксплуатацию
                                </label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="
                                        w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm
                                        [&::-webkit-calendar-picker-indicator]:invert
                                        [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                    "
                                />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Действительно до <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={validUntil}
                                    onChange={(e) => setValidUntil(e.target.value)}
                                    className="
                                        w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm
                                        [&::-webkit-calendar-picker-indicator]:invert
                                        [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                    "
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <p className="text-slate-500 text-xs">
                                Даты действия сохраняются только для статуса
                                «Введен в эксплуатацию».
                            </p>
                        </div>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50"
                    >
                        Отмена
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Сохранение...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                Сохранить
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Карточка узла учёта
// ============================================
const NodeCard = ({ node, openedNodes, toggleNode, goToHouse, setEditStatusNode, groupBadge }) => {
    const statusMeta = STATUS_META[node.status] ?? STATUS_META[0];
    const StatusIcon = statusMeta.icon;
    const dueMeta = getVerificationDueMeta(node.earliestVerificationValidUntil);
    const commissioningDateClass = getCommissioningDateClass(node);
    const daysLeft = getCommissioningDaysLeft(node);
    const isOpen = Boolean(openedNodes[node.nodeId]);
    const deviceCount = node.devices?.length || 0;

    let commissioningTitle = '';
    if (node.status === 1 && node.oplombirovanValidUntil) {
        if (daysLeft !== null) {
            if (daysLeft < 0) {
                commissioningTitle = `Просрочено ${Math.abs(daysLeft)} ${plural(Math.abs(daysLeft), 'день', 'дня', 'дней')} назад`;
            } else {
                commissioningTitle = `Осталось ${daysLeft} ${plural(daysLeft, 'день', 'дня', 'дней')}`;
            }
        }
    }

    return (
        <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 overflow-hidden">
            <div className="flex items-stretch">
                <button
                    type="button"
                    onClick={() => toggleNode(node.nodeId)}
                    className="flex-1 px-4 py-3 flex items-center gap-3 hover:bg-slate-700/30 transition text-left min-w-0"
                >
                    {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    )}
                    <StatusIcon className={`w-5 h-5 flex-shrink-0 ${statusMeta.iconCls}`} />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-white text-sm font-medium">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{formatAddress(node)}</span>
                            {groupBadge && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-[10px] font-medium whitespace-nowrap flex-shrink-0">
                                    <FolderTree className="w-2.5 h-2.5" />
                                    {groupBadge}
                                </span>
                            )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                            <span className="flex items-center gap-1 min-w-0">
                                <Package className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{node.nodeName}</span>
                            </span>
                            <span className="flex items-center gap-1">
                                <Layers className="w-3 h-3 flex-shrink-0" />
                                {deviceCount}{' '}
                                {plural(deviceCount, 'устройство', 'устройства', 'устройств')}
                            </span>
                            {node.earliestVerificationValidUntil ? (
                                <span className={`flex items-center gap-1 ${dueMeta.className}`}>
                                    <CalendarClock className="w-3 h-3 flex-shrink-0" />
                                    ближайшая: {node.earliestVerificationValidUntil}
                                </span>
                            ) : (
                                <span className="flex items-center gap-1 text-slate-500">
                                    <CalendarClock className="w-3 h-3 flex-shrink-0" />
                                    дата поверки не указана
                                </span>
                            )}
                        </div>
                    </div>
                </button>

                <div className="flex items-center gap-2 px-3 border-l border-slate-700/40">
                    <span
                        title={statusMeta.label}
                        className={`
                            shrink-0 px-2 py-1 rounded-full border
                            text-[10px] font-bold whitespace-nowrap
                            max-w-[170px] truncate
                            ${statusMeta.badge}
                        `}
                    >
                        {statusMeta.label}
                    </span>

                    {node.status === 1 && node.oplombirovanDate && node.oplombirovanValidUntil && (
                        <span
                            title={commissioningTitle}
                            className="hidden md:inline-flex px-2 py-1 rounded text-[10px] items-center gap-1 bg-slate-700/40 border border-slate-600/50 whitespace-nowrap"
                        >
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-300">{node.oplombirovanDate}</span>
                            <span className="text-slate-500">—</span>
                            <span className={commissioningDateClass}>
                                {node.oplombirovanValidUntil}
                            </span>
                        </span>
                    )}

                    {node.status === 1 && !node.oplombirovanValidUntil && (
                        <span className="hidden md:inline-flex px-2 py-1 rounded text-[10px] items-center gap-1 bg-yellow-500/15 border border-yellow-500/40 text-yellow-400 whitespace-nowrap">
                            <AlertTriangle className="w-3 h-3" />
                            Срок не задан
                        </span>
                    )}

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            goToHouse(node);
                        }}
                        className="p-1.5 bg-slate-600/60 hover:bg-slate-500 text-slate-200 hover:text-white rounded transition flex-shrink-0"
                        title={`Перейти к дому: ${formatAddress(node)}`}
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditStatusNode(node);
                        }}
                        className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition flex-shrink-0"
                        title="Изменить статус узла"
                    >
                        <Wrench className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {isOpen && (
                <div className="border-t border-slate-700/60 px-4 py-3 bg-slate-900/60">
                    {node.description && (
                        <p className="text-slate-400 text-xs mb-3">{node.description}</p>
                    )}

                    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-400">
                        <span className="flex items-center gap-1">
                            <StatusIcon className={`w-3 h-3 ${statusMeta.iconCls}`} />
                            Статус: <span className="text-slate-200">{statusMeta.label}</span>
                        </span>
                        {node.status === 1 && node.oplombirovanDate && node.oplombirovanValidUntil && (
                            <span title={commissioningTitle} className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Действие:{' '}
                                <span className="text-slate-300">{node.oplombirovanDate}</span>
                                <span className="text-slate-500">—</span>
                                <span className={commissioningDateClass}>
                                    {node.oplombirovanValidUntil}
                                </span>
                            </span>
                        )}
                        {node.status === 1 && !node.oplombirovanValidUntil && (
                            <span className="text-yellow-400 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Срок действия не задан
                            </span>
                        )}
                        {node.earliestVerificationValidUntil && (
                            <span className={dueMeta.className}>
                                Ближайшая поверка: {node.earliestVerificationValidUntil}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => goToHouse(node)}
                            className="
                                ml-auto inline-flex items-center gap-1 px-2 py-1 rounded
                                bg-blue-600/15 hover:bg-blue-600/30 border border-blue-500/30
                                text-blue-300 hover:text-blue-200 text-[11px] font-medium transition
                            "
                            title={`Открыть ${formatAddress(node)} в разделе «Объект учета»`}
                        >
                            <ExternalLink className="w-3 h-3" />
                            Перейти к дому
                        </button>
                    </div>

                    {deviceCount === 0 ? (
                        <div className="text-slate-500 text-sm py-4 text-center">
                            Устройства в этом узле учета не найдены
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[960px] text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                        <th className="pb-2 pr-3 font-semibold">Тип СИ</th>
                                        <th className="pb-2 pr-3 font-semibold">Исполнение</th>
                                        <th className="pb-2 pr-3 font-semibold">Зав. номер</th>
                                        <th className="pb-2 pr-3 font-semibold">Гос. реестр</th>
                                        <th className="pb-2 pr-3 font-semibold">Поверка</th>
                                        <th className="pb-2 font-semibold">Поверка до</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {node.devices.map((device, index) => {
                                        const deviceDueMeta = getVerificationDueMeta(
                                            device.verificationValidUntil
                                        );
                                        let displayTypeName = device.typeName || device.category;
                                        let displayModification = device.modification || '—';
                                        let registryNumber = device.registryNumber || '—';

                                        const tn = (device.typeName || '').toLowerCase();
                                        if (tn === 'aksioma03') {
                                            displayTypeName = 'Аксиома';
                                            displayModification = 'Аксиома-03';
                                            registryNumber = '53677-13';
                                        } else if (tn === 'aksioma01') {
                                            displayTypeName = 'Аксиома';
                                            displayModification = 'Аксиома-01';
                                            registryNumber = '53677-13';
                                        } else if (tn === 'karat307') {
                                            displayTypeName = 'Карат';
                                            displayModification = 'Карат-307';
                                            registryNumber = '61255-15';
                                        }

                                        return (
                                            <tr
                                                key={index}
                                                className="border-t border-slate-700/40 align-top"
                                            >
                                                <td className="py-2.5 pr-3">
                                                    <div className="text-white">{displayTypeName}</div>
                                                    <div className="text-xs text-slate-500">
                                                        {device.category}
                                                    </div>
                                                </td>
                                                <td className="py-2.5 pr-3 text-slate-300">
                                                    {displayModification}
                                                </td>
                                                <td className="py-2.5 pr-3 text-slate-300 font-mono text-xs">
                                                    {device.serialNumber || '—'}
                                                </td>
                                                <td className="py-2.5 pr-3 text-slate-300 font-mono text-xs">
                                                    {registryNumber}
                                                </td>
                                                <td className="py-2.5 pr-3 text-slate-300">
                                                    {device.verificationDate || '—'}
                                                </td>
                                                <td className="py-2.5">
                                                    <div className={deviceDueMeta.className}>
                                                        {device.verificationValidUntil || '—'}
                                                    </div>
                                                    <div className="text-[10px] text-slate-500 mt-0.5">
                                                        {deviceDueMeta.label}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// ✅ Главный компонент
// ============================================
export default function MeteringUnitsTab() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ✅ Группы и доступные устройства
    const [groups, setGroups] = useState([]);
    const [availableDevices, setAvailableDevices] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(true);

    const [yearFilter, setYearFilter] = useState(() => {
        const saved = sessionStorage.getItem('meteringUnitsYearFilter');
        if (saved) {
            sessionStorage.removeItem('meteringUnitsYearFilter');
            return Number(saved);
        }
        return null;
    });

    const [openedNodes, setOpenedNodes] = useState({});
    const [editStatusNode, setEditStatusNode] = useState(null);
    const [savingStatus, setSavingStatus] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

    const loadData = useCallback(async (year) => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const { data: response } = await axios.get(`${API_URL}/api/metering-units`, {
                params: year ? { year } : {},
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setData(response);
        } catch (e) {
            console.error('Ошибка загрузки узлов учета:', e);
            setData(null);
            setError(
                e.response?.data?.message ||
                'Не удалось загрузить список узлов учета'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    // ✅ Загрузка групп + available-devices параллельно
    const loadGroups = useCallback(async () => {
        setGroupsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [groupsRes, devicesRes] = await Promise.all([
                axios.get(`${API_URL}/api/devicegroups`, { headers }),
                axios.get(`${API_URL}/api/devicegroups/available-devices`, { headers }),
            ]);

            setGroups(groupsRes.data || []);
            setAvailableDevices(devicesRes.data || []);
        } catch (e) {
            console.error('Ошибка загрузки групп:', e);
            setGroups([]);
            setAvailableDevices([]);
        } finally {
            setGroupsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData(yearFilter);
    }, [yearFilter, loadData]);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    const handleSaveStatus = async (statusData) => {
        if (!editStatusNode) return;
        if (statusData.status === 1 && !statusData.oplombirovanValidUntil) {
            alert('Для статуса «Введен в эксплуатацию» укажите дату окончания действия');
            return;
        }
        setSavingStatus(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/api/metering-units/${editStatusNode.nodeId}/status`,
                statusData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setEditStatusNode(null);
            await loadData(yearFilter);
        } catch (e) {
            console.error('Ошибка сохранения статуса:', e);
            alert(e.response?.data?.message || 'Не удалось сохранить статус');
        } finally {
            setSavingStatus(false);
        }
    };

    const goToHouse = (node) => {
        localStorage.setItem('selectedHouseFromStats', JSON.stringify({
            city: node.city,
            street: node.street,
            houseNumber: node.houseNumber,
        }));
        navigate('/devices');
    };

    const toggleNode = (nodeId) => {
        setOpenedNodes((prev) => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }));
    };

    const toggleGroup = (groupId) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const items = data?.items || [];
    const summary = data?.summary || {
        all: items.length,
        currentYear: 0,
        nextYear: 0,
    };

    // ✅ Маппинг: deviceName (из группы) → объект устройства (с адресом)
    const deviceMap = useMemo(() => {
        const map = new Map();
        availableDevices.forEach((d) => {
            map.set(d.name, d);
        });
        return map;
    }, [availableDevices]);

    // ✅ Маппинг: ключ адреса (city|street|houseNumber) → группа
    // Это решает проблему разного форматирования имен!
    const addressToGroup = useMemo(() => {
        const map = new Map();
        if (!groups || groups.length === 0) return map;

        groups.forEach((group) => {
            (group.devices || []).forEach((deviceName) => {
                const device = deviceMap.get(deviceName);
                if (device) {
                    // Формируем ключ из адреса устройства
                    const key = `${device.city || ''}|${device.street || ''}|${device.houseNumber || ''}`.toLowerCase().trim();
                    if (key !== '||' && !map.has(key)) {
                        map.set(key, {
                            groupId: group.id,
                            groupName: group.name,
                            groupDescription: group.description,
                        });
                    }
                } else {
                    // Если устройство не найдено в availableDevices, пробуем по имени
                    // (fallback на случай, если имя совпадает с nodeName)
                    console.warn(`[MeteringUnitsTab] Устройство "${deviceName}" из группы "${group.name}" не найдено в available-devices`);
                }
            });
        });
        return map;
    }, [groups, deviceMap]);

    // ✅ Группируем узлы учёта по адресу
    const groupedNodes = useMemo(() => {
        const groupSections = new Map();
        const ungroupedNodes = [];

        // Инициализируем секции для всех групп
        groups.forEach((group) => {
            groupSections.set(group.id, {
                groupId: group.id,
                groupName: group.name,
                groupDescription: group.description,
                nodes: [],
            });
        });

        items.forEach((node) => {
            // Формируем тот же ключ из адреса узла учёта
            const key = `${node.city || ''}|${node.street || ''}|${node.houseNumber || ''}`.toLowerCase().trim();
            const foundGroup = addressToGroup.get(key);

            if (foundGroup && groupSections.has(foundGroup.groupId)) {
                groupSections.get(foundGroup.groupId).nodes.push(node);
            } else {
                ungroupedNodes.push(node);
            }
        });

        return {
            groupSections: Array.from(groupSections.values()).filter((s) => s.nodes.length > 0),
            ungroupedNodes,
        };
    }, [items, groups, addressToGroup]);

    const filters = [
        {
            key: 'all',
            value: null,
            label: 'Все',
            count: summary.all ?? 0,
        },
        {
            key: 'current-year',
            value: data?.currentYear || CURRENT_YEAR,
            label: `${data?.currentYear || CURRENT_YEAR} год`,
            count: summary.currentYear ?? 0,
        },
        {
            key: 'next-year',
            value: data?.nextYear || NEXT_YEAR,
            label: `${data?.nextYear || NEXT_YEAR} год`,
            count: summary.nextYear ?? 0,
        },
    ];

    const hasGroups = groupedNodes.groupSections.length > 0;

    return (
        <div className="bg-slate-800 rounded-xl p-4 sm:p-6 border border-slate-700">
            <div className="flex flex-col gap-4">
                {/* Заголовок раздела */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                        <Wrench className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                        <h3 className="text-white text-lg font-semibold">
                            Поверка узлов учета
                        </h3>
                        <p className="text-slate-400 text-sm">
                            Статусы узлов учета и сроки поверки устройств
                        </p>
                    </div>
                </div>

                {/* Фильтры */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        {filters.map((filter) => {
                            const isActive = yearFilter === filter.value;
                            return (
                                <button
                                    key={filter.key}
                                    type="button"
                                    onClick={() => setYearFilter(filter.value)}
                                    className={`
                                        px-3 py-2 rounded-lg border text-sm font-medium transition
                                        flex items-center gap-2
                                        ${isActive
                                            ? 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                                            : 'bg-slate-700/30 border-slate-600 text-slate-300 hover:bg-slate-700'
                                        }
                                    `}
                                >
                                    {filter.label}
                                    <span className="px-1.5 py-0.5 rounded bg-slate-900/50 text-[10px] font-bold tabular-nums">
                                        {filter.count ?? 0}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        onClick={() => {
                            loadData(yearFilter);
                            loadGroups();
                        }}
                        disabled={loading}
                        className="
                            px-4 py-2 bg-blue-600 hover:bg-blue-500
                            disabled:opacity-60 text-white rounded-lg
                            text-sm font-medium transition
                            flex items-center gap-2
                            shadow-lg shadow-blue-600/20
                        "
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Обновить
                    </button>
                </div>

                {/* Ошибка */}
                {error && (
                    <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </span>
                        <button
                            onClick={() => loadData(yearFilter)}
                            className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap"
                        >
                            Повторить
                        </button>
                    </div>
                )}

                {/* Загрузка */}
                {(loading || groupsLoading) && (
                    <div className="flex flex-col items-center justify-center py-10 gap-3">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                        <div className="text-slate-400 text-xs text-center">
                            Загрузка узлов учета...
                        </div>
                    </div>
                )}

                {/* Пусто */}
                {!loading && !groupsLoading && !error && items.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                        <div className="w-12 h-12 rounded-full bg-slate-600/20 border border-slate-600/40 flex items-center justify-center mb-3">
                            <Inbox className="w-6 h-6 text-slate-500" />
                        </div>
                        <p className="text-slate-300 text-sm font-medium">
                            Узлы учета не найдены
                        </p>
                        {yearFilter !== null && (
                            <button
                                onClick={() => setYearFilter(null)}
                                className="mt-3 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-medium text-slate-300 transition"
                            >
                                Сбросить фильтр года
                            </button>
                        )}
                    </div>
                )}

                {/* Список узлов */}
                {!loading && !groupsLoading && !error && items.length > 0 && (
                    <div className="space-y-3">
                        {/* ===== ГРУППЫ (сворачиваемые секции) ===== */}
                        {hasGroups &&
                            groupedNodes.groupSections.map((section) => {
                                const isCollapsed = collapsedGroups.has(section.groupId);
                                const nodeCount = section.nodes.length;

                                return (
                                    <div
                                        key={`group-${section.groupId}`}
                                        className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 overflow-hidden"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(section.groupId)}
                                            className="
                                                w-full px-4 py-3 flex items-center gap-3
                                                hover:bg-indigo-500/10 transition text-left
                                            "
                                        >
                                            {isCollapsed ? (
                                                <ChevronRight className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                                            )}
                                            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
                                                <Users className="w-4 h-4 text-indigo-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-white text-sm font-semibold truncate">
                                                        {section.groupName}
                                                    </span>
                                                    <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-[10px] font-bold tabular-nums whitespace-nowrap">
                                                        {nodeCount} {plural(nodeCount, 'узел', 'узла', 'узлов')}
                                                    </span>
                                                </div>
                                                {section.groupDescription && (
                                                    <p className="text-indigo-300/60 text-[11px] mt-0.5 truncate">
                                                        {section.groupDescription}
                                                    </p>
                                                )}
                                            </div>
                                            <FolderTree className="w-4 h-4 text-indigo-400/50 flex-shrink-0" />
                                        </button>

                                        {!isCollapsed && (
                                            <div className="px-3 pb-3 space-y-2">
                                                {section.nodes.map((node) => (
                                                    <NodeCard
                                                        key={node.nodeId}
                                                        node={node}
                                                        openedNodes={openedNodes}
                                                        toggleNode={toggleNode}
                                                        goToHouse={goToHouse}
                                                        setEditStatusNode={setEditStatusNode}
                                                        groupBadge={section.groupName}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                        {/* ===== УЗЛЫ БЕЗ ГРУППЫ ===== */}
                        {groupedNodes.ungroupedNodes.length > 0 && (
                            <>
                                {hasGroups && (
                                    <div className="flex items-center gap-3 pt-1">
                                        <div className="h-px flex-1 bg-slate-700/60" />
                                        <span className="text-slate-500 text-[10px] font-medium uppercase tracking-wider whitespace-nowrap">
                                            Без группы
                                        </span>
                                        <div className="h-px flex-1 bg-slate-700/60" />
                                    </div>
                                )}

                                <div className="space-y-2">
                                    {groupedNodes.ungroupedNodes.map((node) => (
                                        <NodeCard
                                            key={node.nodeId}
                                            node={node}
                                            openedNodes={openedNodes}
                                            toggleNode={toggleNode}
                                            goToHouse={goToHouse}
                                            setEditStatusNode={setEditStatusNode}
                                            groupBadge={null}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {editStatusNode && (
                <EditStatusModal
                    node={editStatusNode}
                    saving={savingStatus}
                    onClose={() => !savingStatus && setEditStatusNode(null)}
                    onSave={handleSaveStatus}
                />
            )}
        </div>
    );
}